import React from 'react';
import { ToolbarButton } from '@/components/creator/partials/toolbarButton/ToolbarButton';
import { useMediaQuery } from '@mantine/hooks';
import { Flex } from '@mantine/core';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import MeasureIcon from '@/assets/icons/measure.svg?react';
import ZoomInIcon from '@/assets/icons/zoomIn.svg?react';
import ZoomOutIcon from '@/assets/icons/zoomOut.svg?react';
import HelpIcon from '@/assets/icons/help.svg?react';
import UndoIcon from '@/assets/icons/undo.svg?react';
import CenterIcon from '@/assets/icons/centerRoom.svg?react';
import APP_CONFIG from '@/configs/appConfig';
import { useContent } from '@/providers/ContentProvider';
import styles from './Toolbar.module.scss';

const baseSlug = 'toolbar.';

const renderer = ProjectRenderer.getInstance();

interface Props {
	zoomEnabled: {
		in: boolean;
		out: boolean;
	};
	handleZoom: (zoomAction: 'in' | 'out') => () => void;
	toggleFullscreen: () => Promise<void>;
	handleCenterView: () => void;
}

export const Toolbar: React.FC<Props> = ({
	zoomEnabled,
	handleZoom,
	toggleFullscreen,
	handleCenterView,
}) => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options, snapshots, selectedProductModuleCollection } =
		useTypedSelector((state) => state.creator);

	const isMobile = useMediaQuery(`(max-width: ${APP_CONFIG.BREAKPOINTS.MD})`);

	const toggleMeasureVisibility = () => {
		dispatch(
			creatorActions.updateOptions({
				isMetersVisible: !options.isMetersVisible,
			})
		);
		renderer.toggleBoxMeasuresVisibility();
	};

	const toggleInstructionModal = () => {
		dispatch(
			creatorActions.updateOptions({
				instructionModalOpened: !options.instructionModalOpened,
			})
		);
	};

	const undo = () => {
		dispatch(creatorActions.undo());
	};

	const buttons = isMobile ? (
		<>
			<ToolbarButton
				Icon={CenterIcon}
				tooltipProps={{ opened: false, label: '' }}
				onClick={toggleFullscreen}
			/>
			<ToolbarButton
				Icon={MeasureIcon}
				tooltipProps={{
					label: options.isMetersVisible
						? (getContent(`${baseSlug}hideDimensions`) as string)
						: (getContent(`${baseSlug}showDimensions`) as string),
				}}
				onClick={toggleMeasureVisibility}
				disabled={!selectedProductModuleCollection}
			/>
		</>
	) : (
		<>
			{!options.isPreviewMode && (
				<ToolbarButton
					Icon={HelpIcon}
					tooltipProps={{
						label: getContent(`${baseSlug}instruction`) as string,
					}}
					onClick={toggleInstructionModal}
					disabled={!zoomEnabled.in}
					className={styles.instructionsButton}
				/>
			)}
			<ToolbarButton
				Icon={ZoomInIcon}
				tooltipProps={{ label: getContent(`${baseSlug}zoomIn`) as string }}
				onClick={handleZoom('in')}
				disabled={!zoomEnabled.in || !selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={ZoomOutIcon}
				tooltipProps={{ label: getContent(`${baseSlug}zoomOut`) as string }}
				onClick={handleZoom('out')}
				disabled={!zoomEnabled.out || !selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={CenterIcon}
				tooltipProps={{ label: getContent(`${baseSlug}center`) as string }}
				onClick={handleCenterView}
				disabled={!selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={MeasureIcon}
				tooltipProps={{
					label: options.isMetersVisible
						? (getContent(`${baseSlug}hideDimensions`) as string)
						: (getContent(`${baseSlug}showDimensions`) as string),
				}}
				onClick={toggleMeasureVisibility}
				disabled={!selectedProductModuleCollection}
			/>
			{!options.isPreviewMode && (
				<ToolbarButton
					Icon={UndoIcon}
					tooltipProps={{ label: getContent(`${baseSlug}undo`) as string }}
					onClick={undo}
					disabled={!snapshots.length}
				/>
			)}
		</>
	);

	return (
		<Flex gap="sm" className={styles.container}>
			{buttons}
		</Flex>
	);
};
